import React, { useEffect } from 'react';

const CookiePolicy = () => {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const styles = {
        container: {
            maxWidth: '1000px',
            margin: '0 auto',
            padding: '120px 24px 60px',
            color: '#414141',
            fontFamily: 'Mulish, sans-serif',
        },
        header: {
            marginBottom: '40px',
            borderBottom: '1px solid #eee',
            paddingBottom: '20px',
        },
        h1: {
            fontSize: '36px',
            fontWeight: '800',
            color: '#2B2B2B',
            marginBottom: '10px',
        },
        lastUpdated: {
            color: '#6E6E6E',
            fontSize: '14px',
        },
        section: {
            marginBottom: '40px',
        },
        h2: {
            fontSize: '24px',
            fontWeight: '700',
            color: '#D81F26', /* Kirusa Red */
            marginBottom: '16px',
            marginTop: '32px',
        },
        h3: {
            fontSize: '18px',
            fontWeight: '600',
            color: '#3457D5', /* Corpusio Blue */
            marginBottom: '12px',
            marginTop: '24px',
        },
        p: {
            marginBottom: '16px',
            lineHeight: '1.7',
            fontSize: '16px',
        },
        ul: {
            marginBottom: '16px',
            paddingLeft: '24px',
        },
        li: {
            marginBottom: '8px',
            lineHeight: '1.7',
        },
        link: {
            color: '#3457D5',
            textDecoration: 'underline',
        }
    };

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <h1 style={styles.h1}>Cookie Policy</h1>
                <div style={styles.lastUpdated}>Last Updated: January 31, 2026</div>
            </div>

            <div style={styles.section}>
                <p style={styles.p}>Kirusa Inc. is committed to your privacy worldwide, for every service and every user.</p>

                <h2 style={styles.h2}>Overview</h2>
                <p style={styles.p}>
                    This Cookie Policy explains how Kirusa Inc. (“Kirusa,” “we,” “us,” or “our”) uses cookies and similar technologies when you visit or use https://corpusio.com/ (the “Site”) and when you use Corpusio — an AI-powered document intelligence and natural-language search service that can be accessed via web, mobile, and messaging channels such as WhatsApp (the “Services”).
                </p>
                <p style={styles.p}>
                    This Cookie Policy should be read together with our Privacy Policy and Terms of Service. Where required by applicable law, we will request your consent before placing non-essential cookies on your device, and we provide options to manage cookie preferences.
                </p>

                <h2 style={styles.h2}>1. What Are Cookies and Similar Technologies?</h2>
                <p style={styles.p}>
                    Cookies are small text files placed on your device (computer, phone, tablet) by websites you visit. Cookies allow a site to recognize your device and store certain information about your preferences or actions.
                </p>
                <p style={styles.p}>We may also use similar technologies, including:</p>
                <ul style={styles.ul}>
                    <li style={styles.li}>Pixels/web beacons (small images or code that help measure page views or email opens)</li>
                    <li style={styles.li}>Local storage (browser-based storage for preferences)</li>
                    <li style={styles.li}>SDKs (software development kits) in mobile applications for analytics, performance, and security</li>
                    <li style={styles.li}>Device identifiers (advertising IDs or app instance IDs where applicable)</li>
                </ul>

                <h2 style={styles.h2}>2. Why We Use Cookies</h2>
                <p style={styles.p}>We use cookies and similar technologies to:</p>
                <ul style={styles.ul}>
                    <li style={styles.li}><strong>Strictly Necessary Purposes:</strong> Enable core site functionality such as page navigation, account login, authentication, session management, security, and fraud prevention.</li>
                    <li style={styles.li}><strong>Performance and Analytics:</strong> Understand how visitors use the Site and Services, measure performance, identify errors, and improve reliability and user experience.</li>
                    <li style={styles.li}><strong>Functionality:</strong> Remember preferences (e.g., language, region, settings) and provide enhanced features.</li>
                    <li style={styles.li}><strong>Security:</strong> Detect abuse, protect accounts, and maintain the integrity of the Site and Services.</li>
                    <li style={styles.li}><strong>Marketing (Optional):</strong> Deliver and measure marketing campaigns and personalize content and advertisements, where permitted by law and where you have not opted out or where you have provided consent.</li>
                </ul>

                <h2 style={styles.h2}>3. Types of Cookies We Use</h2>
                <p style={styles.p}>Depending on your region and how you use the Site and Services, we may use:</p>

                <h3 style={styles.h3}>3.1 Strictly Necessary Cookies</h3>
                <p style={styles.p}>
                    These cookies are required for the Site and Services to function and cannot be switched off in our systems. They are usually set in response to actions you take such as logging in, setting privacy preferences, or filling in forms. Disabling these cookies may affect core functionality.
                </p>

                <h3 style={styles.h3}>3.2 Performance & Analytics Cookies</h3>
                <p style={styles.p}>
                    These cookies help us understand usage patterns, such as which pages are most popular and how users move around the Site. This information helps us improve the Site and Services. Where required by law, these cookies are placed only with your consent.
                </p>

                <h3 style={styles.h3}>3.3 Functional Cookies</h3>
                <p style={styles.p}>
                    These cookies allow the Site to remember choices you make (such as language or region) and provide enhanced, more personalized features.
                </p>

                <h3 style={styles.h3}>3.4 Advertising / Targeting Cookies (Optional)</h3>
                <p style={styles.p}>
                    These cookies may be set through the Site by us or our advertising partners. They may be used to build a profile of your interests and show you relevant advertisements on other sites. Where required by law, these cookies are placed only with your consent, and you can opt out as described below.
                </p>

                <h2 style={styles.h2}>4. Cookie Duration</h2>
                <p style={styles.p}>Cookies may be:</p>
                <ul style={styles.ul}>
                    <li style={styles.li}><strong>Session Cookies:</strong> Temporary cookies that expire when you close your browser.</li>
                    <li style={styles.li}><strong>Persistent Cookies:</strong> Cookies that remain on your device until they expire or are deleted.</li>
                </ul>

                <h2 style={styles.h2}>5. How to Manage Cookies</h2>
                <p style={styles.p}>You can control and manage cookies in several ways:</p>

                <h3 style={styles.h3}>5.1 Browser Controls</h3>
                <p style={styles.p}>Most browsers let you:</p>
                <ul style={styles.ul}>
                    <li style={styles.li}>View what cookies are stored</li>
                    <li style={styles.li}>Delete cookies</li>
                    <li style={styles.li}>Block cookies</li>
                    <li style={styles.li}>Set preferences for certain sites</li>
                </ul>
                <p style={styles.p}>
                    If you block all cookies, parts of the Site or Services may not work correctly.
                </p>

                <h3 style={styles.h3}>5.2 Cookie Banner / Preference Center (Where Available)</h3>
                <p style={styles.p}>
                    Where required by law (e.g., EU/EEA/UK), we provide a cookie consent banner or preference center allowing you to accept, reject, or customize non-essential cookies.
                </p>

                <h3 style={styles.h3}>5.3 Opt-Out of Interest-Based Advertising</h3>
                <p style={styles.p}>If we use advertising/targeting cookies, you can opt out via:</p>
                <ul style={styles.ul}>
                    <li style={styles.li}><a href="http://www.networkadvertising.org/understanding-online-advertising/how-does-it-work" style={styles.link} target="_blank" rel="noopener noreferrer">Network Advertising Initiative</a></li>
                    <li style={styles.li}><a href="http://optout.aboutads.info/" style={styles.link} target="_blank" rel="noopener noreferrer">Digital Advertising Alliance</a></li>
                    <li style={styles.li}><a href="https://www.google.com/settings/ads/anonymous" style={styles.link} target="_blank" rel="noopener noreferrer">Google Ads</a></li>
                    <li style={styles.li}><a href="https://www.facebook.com/settings/?tab=ads" style={styles.link} target="_blank" rel="noopener noreferrer">Facebook</a></li>
                </ul>
                <p style={styles.p}>Opting out does not stop all ads, only targeted ones.</p>

                <h2 style={styles.h2}>6. Cookies Used by Third Parties</h2>
                <p style={styles.p}>
                    Some cookies may be set by third parties (for example, analytics providers, embedded content providers, or advertising partners). These third parties may collect information about your online activities over time and across different websites.
                </p>
                <p style={styles.p}>
                    Links on our Site may take you to third-party websites. Their cookie practices are their own; please review their policies.
                </p>

                <h2 style={styles.h2}>7. Do Not Track</h2>
                <p style={styles.p}>
                    We currently do not change our data collection practices in response to browser “Do Not Track” signals. We do honour advertising opt-outs and comply with applicable consent and objection rights under GDPR, DPDP Act, CCPA/CPRA, and relevant African data protection laws.
                </p>

                <h2 style={styles.h2}>8. Messaging Channels and Mobile SDKs</h2>
                <p style={styles.p}>
                    If you use Corpusio via mobile applications or messaging channels (such as WhatsApp where offered), we may use SDKs and similar technologies to support functionality, security, and analytics within those environments. These technologies may have their own settings and controls depending on your device and platform.
                </p>

                <h2 style={styles.h2}>9. Updates to This Cookie Policy</h2>
                <p style={styles.p}>
                    We may update this Cookie Policy from time to time. The “Last Updated” date shows the latest version. Material changes may be announced on the Site or by other legally required methods.
                </p>

                <h2 style={styles.h2}>10. Contact Us</h2>
                <p style={styles.p}>
                    <strong>Email:</strong> <a href="mailto:support-corpusio@kirusa.com" style={styles.link}>support-corpusio@kirusa.com</a>
                </p>
                <p style={styles.p}>
                    <strong>Address:</strong> Kirusa Inc., 85 Swenson Circle, Berkeley Heights, NJ 07922, USA
                </p>

                <div style={{ marginTop: '60px', borderTop: '1px solid #eee', paddingTop: '20px', fontSize: '14px', color: '#6E6E6E' }}>
                    &copy; 2026 Kirusa Inc. All rights reserved.
                </div>
            </div>
        </div>
    );
};

export default CookiePolicy;
